/**
 * ====================================================================
 * FIKO CAD DIMENSION SYSTEM - TYPE DEFINITIONS
 * ====================================================================
 *
 * CATIA/IronCAD/SolidWorks seviyesinde profesyonel ölçülendirme sistemi
 * için tip tanımları.
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { ExtendedArrowStyle } from './visual/DimensionVisualStyle';

// ====================================================================
// TEMEL TİPLER
// ====================================================================

/**
 * Ölçü tipi enum - Her tip için farklı çizim stratejisi
 */
export enum DimensionType {
    LINEAR = 'LINEAR',           // İki nokta arası düz mesafe
    HORIZONTAL = 'HORIZONTAL',   // Yatay (X-axis locked) mesafe
    VERTICAL = 'VERTICAL',       // Dikey (Y-axis locked) mesafe
    ALIGNED = 'ALIGNED',         // Hizalanmış (çizgi boyunca) mesafe
    ANGULAR = 'ANGULAR',         // Açı ölçüsü (derece)
    DIAMETER = 'DIAMETER',       // Çap ölçüsü (⌀)
    RADIUS = 'RADIUS',           // Yarıçap ölçüsü (R)
    ARC_LENGTH = 'ARC_LENGTH',   // Yay uzunluğu
    ORDINATE = 'ORDINATE',       // Koordinat ölçüsü (X veya Y)
    CHAIN = 'CHAIN',             // Zincir ölçü (ardışık)
    BASELINE = 'BASELINE'        // Baseline ölçü (ortak referans)
}

/**
 * Referans tipi - Neyin seçildiğini belirtir
 */
export enum ReferenceType {
    POINT = 'POINT',             // Tek nokta
    EDGE = 'EDGE',               // Kenar (çizgi)
    FACE = 'FACE',               // Yüzey
    CIRCLE = 'CIRCLE',           // Daire/çember
    ARC = 'ARC',                 // Yay
    CYLINDER = 'CYLINDER',       // Silindir yüzeyi
    CONE = 'CONE',               // Koni yüzeyi
    SPHERE = 'SPHERE',           // Küre yüzeyi
    VERTEX = 'VERTEX'            // Köşe noktası
}

/**
 * Axis lock enum - Hangi eksene kilitlenecek
 */
export enum AxisLock {
    NONE = 'NONE',
    X = 'X',
    Y = 'Y',
    Z = 'Z',
    XY_PLANE = 'XY_PLANE',
    XZ_PLANE = 'XZ_PLANE',
    YZ_PLANE = 'YZ_PLANE'
}

/**
 * Text placement enum - Yazı nerede olacak
 */
export enum TextPlacement {
    ABOVE = 'ABOVE',             // Çizginin üstünde
    BELOW = 'BELOW',             // Çizginin altında
    CENTER = 'CENTER',           // Çizginin ortasında (inline)
    LEFT = 'LEFT',               // Sol tarafta
    RIGHT = 'RIGHT',             // Sağ tarafta
    AUTO = 'AUTO'                // Otomatik (en uygun)
}

/**
 * Arrow style enum - Ok başı stili
 */
export enum ArrowStyle {
    FILLED = 'FILLED',           // Dolu ok (CAD standardı)
    OPEN = 'OPEN',               // Açık ok
    DOT = 'DOT',                 // Nokta
    TICK = 'TICK',               // Çizgi (architectural)
    NONE = 'NONE'                // Ok yok
}

/**
 * Dimension state enum - Ölçünün durumu
 */
export enum DimensionState {
    NORMAL = 'NORMAL',
    HOVERED = 'HOVERED',
    SELECTED = 'SELECTED',
    DRAGGING = 'DRAGGING',
    EDITING = 'EDITING',
    HIDDEN = 'HIDDEN'
}

// ====================================================================
// REFERANS INTERFACE'LERİ
// ====================================================================

/**
 * Geometri referansı - Ölçümün bağlandığı geometri
 */
export interface GeometryReference {
    type: ReferenceType;
    object?: THREE.Object3D;           // Kaynak Three.js objesi
    mesh?: THREE.Mesh;                 // Kaynak mesh
    point?: THREE.Vector3;             // Nokta pozisyonu (world space)
    normal?: THREE.Vector3;            // Yüzey normali
    direction?: THREE.Vector3;         // Kenar yönü
    center?: THREE.Vector3;            // Merkez noktası (daire/silindir için)
    radius?: number;                   // Yarıçap (daire/silindir için)
    startAngle?: number;               // Başlangıç açısı (yay için)
    endAngle?: number;                 // Bitiş açısı (yay için)
    axis?: THREE.Vector3;              // Eksen yönü (silindir için)
    faceIndex?: number;                // Face index
    edgeIndex?: number;                // Edge index
    vertexIndex?: number;              // Vertex index
    surfacePoint?: THREE.Vector3;      // Gerçek yüzey noktası (eğri yüzeylerde ok pozisyonu için)
}

/**
 * İki noktalı referans (Linear, Aligned için)
 */
export interface TwoPointReference {
    refA: GeometryReference;
    refB: GeometryReference;
}

/**
 * Tek noktalı referans (Diameter, Radius için)
 */
export interface SingleReference {
    ref: GeometryReference;
}

/**
 * Üç noktalı referans (Angular için)
 */
export interface ThreePointReference {
    refA: GeometryReference;  // Açının bir kolu
    refB: GeometryReference;  // Açının merkezi (vertex)
    refC: GeometryReference;  // Açının diğer kolu
}

// ====================================================================
// DIMENSION OPTIONS
// ====================================================================

/**
 * Temel dimension seçenekleri (tüm tipler için ortak)
 */
export interface BaseDimensionOptions {
    // Görünüm
    color?: number;                    // Çizgi rengi (hex)
    textColor?: string;                // Yazı rengi (CSS)
    backgroundColor?: string;          // 🎯 v3.2.0: Arkaplan rengi (CSS)
    backgroundOpacity?: number;        // 🎯 v3.2.6: Arkaplan opaklığı (0-1)
    lineWidth?: number;                // Çizgi kalınlığı (px)
    fontSize?: number;                 // Yazı boyutu (px)
    fontFamily?: string;               // Font ailesi

    // Yerleşim
    offset?: number;                   // Geometriden uzaklık
    textPlacement?: TextPlacement;     // Yazı konumu
    textOffset?: number;               // Yazının çizgiden uzaklığı

    // Oklar
    arrowStyle?: ArrowStyle | ExtendedArrowStyle;  // Ok stili (legacy + extended)
    arrowSize?: number;                // Ok boyutu
    arrowsInside?: boolean;            // Oklar içeride mi?

    // Extension lines
    showExtensionLines?: boolean;      // Extension line göster
    extensionLineGap?: number;         // Geometriden gap
    extensionLineOvershoot?: number;   // Dimension line'ı geçme miktarı

    // Davranış
    draggable?: boolean;               // Sürüklenebilir
    selectable?: boolean;              // Seçilebilir
    editable?: boolean;                // Düzenlenebilir

    // Görünürlük
    visible?: boolean;                 // Görünür mü
    layer?: string;                    // Layer adı

    // Değer formatlama
    precision?: number;                // Ondalık hassasiyeti
    unit?: string;                     // Birim (mm, cm, m, in, ft)
    prefix?: string;                   // Ön ek (⌀, R, vb.)
    suffix?: string;                   // Son ek
    showUnit?: boolean;                // Birimi göster

    // Tolerans
    tolerance?: {
        upper?: number;                // Üst tolerans
        lower?: number;                // Alt tolerans
        symmetric?: boolean;           // Simetrik mi (±)
    };

    // Metadata
    id?: string;                       // Unique ID
    name?: string;                     // Kullanıcı adı
    tags?: string[];                   // Etiketler
    userData?: Record<string, any>;    // Ek veri
}

/**
 * Linear dimension için özel seçenekler
 */
export interface LinearDimensionOptions extends BaseDimensionOptions {
    axisLock?: AxisLock;               // Eksen kilidi
    projectionPlane?: THREE.Plane;     // Projeksiyon düzlemi
    measureType?: 'direct' | 'projected';  // Ölçüm tipi
}

/**
 * Angular dimension için özel seçenekler
 */
export interface AngularDimensionOptions extends BaseDimensionOptions {
    arcRadius?: number;                // Yay yarıçapı (sabit veya auto)
    showRefLines?: boolean;            // Referans çizgileri göster
    angleFormat?: 'degrees' | 'radians' | 'dms';  // Açı formatı
    complementary?: boolean;           // Tamamlayıcı açı (180-θ)
    quadrant?: 1 | 2 | 3 | 4;          // Hangi kadran
}

/**
 * Diameter/Radius dimension için özel seçenekler
 */
export interface DiameterDimensionOptions extends BaseDimensionOptions {
    leaderAngle?: number;              // Leader açısı (derece)
    leaderLength?: number;             // Leader uzunluğu
    showCenterMark?: boolean;          // Merkez işareti göster
    centerMarkSize?: number;           // Merkez işareti boyutu
    placement?: 'inside' | 'outside';  // İçerde mi dışarıda mı
}

// ====================================================================
// DIMENSION RESULT
// ====================================================================

/**
 * Dimension oluşturma sonucu
 */
export interface DimensionResult {
    success: boolean;
    dimension?: Dimension;
    error?: DimensionError;
    fallback?: FallbackInfo;
}

/**
 * Dimension hata tipi
 */
export enum DimensionErrorType {
    INVALID_REFERENCE = 'INVALID_REFERENCE',
    COINCIDENT_POINTS = 'COINCIDENT_POINTS',
    PARALLEL_LINES = 'PARALLEL_LINES',
    NO_INTERSECTION = 'NO_INTERSECTION',
    OCCLUSION = 'OCCLUSION',
    OUT_OF_BOUNDS = 'OUT_OF_BOUNDS',
    INTERNAL_ERROR = 'INTERNAL_ERROR'
}

/**
 * Dimension hatası
 */
export interface DimensionError {
    type: DimensionErrorType;
    message: string;
    details?: any;
}

/**
 * Fallback bilgisi
 */
export interface FallbackInfo {
    applied: boolean;
    reason: string;
    originalPlacement?: any;
    fallbackPlacement?: any;
}

// ====================================================================
// DIMENSION ABSTRACT CLASS
// ====================================================================

/**
 * Abstract Dimension class - Tüm dimension tipleri için temel
 */
export abstract class Dimension extends THREE.Group {
    // Temel özellikler
    readonly dimensionType: DimensionType;
    protected _value: number = 0;
    protected _options: BaseDimensionOptions;
    protected _state: DimensionState = DimensionState.NORMAL;

    // Geometri elemanları
    protected dimensionLine: THREE.Object3D | null = null;
    protected extensionLines: THREE.Object3D[] = [];
    protected arrows: THREE.Object3D[] = [];
    protected textSprite: THREE.Sprite | null = null;

    // Referanslar
    protected references: GeometryReference[] = [];

    // Event callbacks
    onValueChange?: (value: number) => void;
    onStateChange?: (state: DimensionState) => void;

    constructor(type: DimensionType, options: BaseDimensionOptions = {}) {
        super();
        this.dimensionType = type;
        this._options = this.mergeWithDefaults(options);

        // Three.js ayarları
        this.renderOrder = Infinity;
        this.frustumCulled = false;

        // Metadata
        this.name = options.name || `Dimension_${type}_${Date.now()}`;
        this.userData.dimensionId = options.id || this.uuid;
        this.userData.dimensionType = type;
    }

    /**
     * Ölçüm değeri
     */
    get value(): number {
        return this._value;
    }

    /**
     * Durum
     */
    get state(): DimensionState {
        return this._state;
    }

    set state(value: DimensionState) {
        if (this._state !== value) {
            this._state = value;
            this.updateAppearance();
            this.onStateChange?.(value);
        }
    }

    /**
     * Seçenekler
     */
    get options(): BaseDimensionOptions {
        return { ...this._options };
    }

    /**
     * Varsayılan değerlerle birleştir
     */
    protected mergeWithDefaults(options: BaseDimensionOptions): BaseDimensionOptions {
        return {
            // 🎯 v3.2.0: Görünüm varsayılanları - SİYAH çizgi/ok, BEYAZ yazı, SİYAH arkaplan
            color: 0x000000,           // Çizgi ve ok rengi: SİYAH
            textColor: '#ffffff',      // Yazı rengi: BEYAZ
            backgroundColor: '#000000', // Arkaplan rengi: SİYAH
            lineWidth: 1.5,
            fontSize: 12,
            fontFamily: 'Arial, sans-serif',

            // Yerleşim varsayılanları
            offset: 8,
            textPlacement: TextPlacement.AUTO,
            textOffset: 2,

            // Ok varsayılanları - CATIA tarzı büyük oklar
            arrowStyle: ArrowStyle.FILLED,
            arrowSize: 0.6,
            arrowsInside: true,

            // Extension line varsayılanları
            showExtensionLines: true,
            extensionLineGap: 1,
            extensionLineOvershoot: 1,

            // Davranış varsayılanları
            draggable: true,
            selectable: true,
            editable: false,

            // Görünürlük varsayılanları
            visible: true,
            layer: 'dimensions',

            // Değer formatlama varsayılanları
            precision: 2,
            unit: 'mm',
            prefix: '',
            suffix: '',
            showUnit: true,

            // Kullanıcı değerleri üzerine yaz
            ...options
        };
    }

    /**
     * Abstract metodlar - Alt sınıflar implemente etmeli
     */
    abstract create(): DimensionResult;
    abstract update(): void;
    abstract validate(): boolean;
    protected abstract calculateValue(): number;
    protected abstract buildGeometry(): void;
    protected abstract updateAppearance(): void;

    /**
     * 🎯 v2.0.0: Offset değerini güncelle
     * CATIA tarzı sürükleme için: Orijinal noktalar sabit kalır, sadece offset değişir
     * Alt sınıflar override edebilir
     *
     * 🎯 v2.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
     * Eski: Math.max(5, newOffset) - sadece pozitif yöne izin veriyordu
     * Yeni: Sınırsız - CATIA/SolidWorks gibi her iki yöne sürüklenebilir
     *
     * @param newOffset Yeni offset değeri (negatif veya pozitif)
     */
    setOffset(newOffset: number): void {
        this._options.offset = newOffset;
        this.buildGeometry();
    }

    /**
     * 🎯 v2.0.0: Mevcut offset değerini al
     */
    getOffset(): number {
        return this._options.offset || 8;
    }

    /**
     * 🎯 v2.0.0: Offset yönünü al (sürükleme için)
     * Bu yön, dimension'ın hangi yöne sürükleneceğini belirler.
     * Kamera bağımsızdır - dimension'ın geometrisine göre hesaplanır.
     *
     * Alt sınıflar bu metodu override ederek kendi offset yönlerini döndürebilir.
     * Varsayılan olarak yukarı yön (Y+) döndürülür.
     *
     * @returns Normalize edilmiş offset yön vektörü
     */
    getOffsetDirection(): THREE.Vector3 {
        // Varsayılan: Yukarı yön
        return new THREE.Vector3(0, 1, 0);
    }

    /**
     * 🎯 v2.0.2: Dimension'ı döndür (radyan cinsinden)
     * Ölçüm ekseni etrafında döndürür
     * Alt sınıflar bu metodu override ederek kendi döndürme mantıklarını uygulayabilir.
     *
     * @param angle Döndürme açısı (radyan)
     */
    setRotation(angle: number): void {
        // Varsayılan implementasyon - alt sınıflar override edebilir
        this.buildGeometry();
    }

    /**
     * 🎯 v2.0.2: Mevcut döndürme açısını al (radyan)
     */
    getRotation(): number {
        return 0;
    }

    /**
     * 🎯 v2.0.2: Dimension'ı belirli bir açı kadar döndür (radyan)
     * @param deltaAngle Eklenecek açı (radyan)
     */
    rotate(deltaAngle: number): void {
        this.setRotation(this.getRotation() + deltaAngle);
    }

    /**
     * 🎯 v2.0.2: Dimension'ı derece cinsinden döndür
     * @param degrees Döndürme açısı (derece)
     */
    setRotationDegrees(degrees: number): void {
        this.setRotation(degrees * Math.PI / 180);
    }

    /**
     * 🎯 v2.0.2: Mevcut döndürme açısını derece olarak al
     */
    getRotationDegrees(): number {
        return this.getRotation() * 180 / Math.PI;
    }

    /**
     * Temizlik
     */
    dispose(): void {
        // Geometrileri temizle
        this.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry?.dispose();
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m.dispose());
                } else {
                    child.material?.dispose();
                }
            }
            if (child instanceof THREE.Sprite) {
                child.material?.map?.dispose();
                child.material?.dispose();
            }
        });

        // Referansları temizle
        this.references = [];

        // Parent'tan kaldır
        this.parent?.remove(this);
    }

    /**
     * Değeri formatla
     */
    protected formatValue(value: number): string {
        const { precision, unit, prefix, suffix, showUnit, tolerance } = this._options;

        let text = value.toFixed(precision || 2);

        if (prefix) {
            text = prefix + text;
        }

        if (showUnit && unit) {
            text += ' ' + unit;
        }

        if (suffix) {
            text += suffix;
        }

        if (tolerance) {
            if (tolerance.symmetric && tolerance.upper !== undefined) {
                text += ` ±${tolerance.upper.toFixed(precision || 2)}`;
            } else {
                if (tolerance.upper !== undefined) {
                    text += ` +${tolerance.upper.toFixed(precision || 2)}`;
                }
                if (tolerance.lower !== undefined) {
                    text += ` -${Math.abs(tolerance.lower).toFixed(precision || 2)}`;
                }
            }
        }

        return text;
    }
}

// ====================================================================
// LAYOUT TYPES
// ====================================================================

/**
 * Bounding box for dimension text/arrows
 */
export interface DimensionBounds {
    min: THREE.Vector2;
    max: THREE.Vector2;
    center: THREE.Vector2;
}

/**
 * Overlap detection result
 */
export interface OverlapResult {
    overlaps: boolean;
    overlappingWith?: Dimension[];
    severity?: 'minor' | 'major' | 'critical';
}

/**
 * Placement suggestion
 */
export interface PlacementSuggestion {
    offset: number;
    angle: number;
    textPlacement: TextPlacement;
    score: number;  // 0-1, higher is better
}

// ====================================================================
// CONSTANTS
// ====================================================================

export const DIMENSION_CONSTANTS = {
    // Minimum değerler
    MIN_DISTANCE: 0.001,          // mm cinsinden minimum mesafe
    MIN_ANGLE: 0.1,               // Derece cinsinden minimum açı
    MIN_RADIUS: 0.001,            // mm cinsinden minimum yarıçap

    // Varsayılan değerler
    DEFAULT_OFFSET: 8,
    DEFAULT_LINE_WIDTH: 1.5,           // Daha kalın çizgi
    DEFAULT_FONT_SIZE: 9,              // Kompakt yazı boyutu
    DEFAULT_ARROW_SIZE: 0.6,           // Daha büyük ok (CATIA tarzı)
    DEFAULT_PRECISION: 2,

    // Performans limitleri
    MAX_DIMENSIONS_PER_SCENE: 1000,
    UPDATE_THROTTLE_MS: 16,        // ~60fps

    // Renk paleti
    COLORS: {
        NORMAL: 0x3A3A3A,
        HOVERED: 0x0066CC,
        SELECTED: 0xFF6600,
        ERROR: 0xFF0000
    }
};
